/**
 * ViewSwitcher — tab-style switch between the two representations of the same
 * fold (the list and the knowledge map).
 *
 * It carried a third, "Änderungen", which showed the attestations that had
 * changed since the last publish. The new model has no such state: a skill has
 * a `state` and a `visibility`, and there is no accepted-versus-published diff
 * to compute. The option went with the DiffView behind it.
 *
 * A local, token-backed segmented control. The shared
 * `@frontend/components/ui/segmented-control` hardcodes neutral-* colours and a
 * `shadow-sm` — both violate this app's DESIGN.md contract (single accent, no
 * shadow, token surfaces). Restyling the shared primitive would change every
 * other app that consumes it, so this stays local: the active segment is the
 * flat surface-bg pill on a muted track, marked by the Action-Blue text +
 * aria-selected (never colour-only).
 */

import { useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { cn } from '@/lib/utils'

export type KnowledgeProfileView = 'list' | 'map'

interface ViewSwitcherProps {
  value: KnowledgeProfileView
  onChange: (value: KnowledgeProfileView) => void
  className?: string
}

export function ViewSwitcher({ value, onChange, className }: ViewSwitcherProps) {
  const { t } = useTranslation()

  // Memoised on `t` so the option list is not reallocated every render; it
  // changes only when the active locale changes.
  const OPTIONS = useMemo<{ id: KnowledgeProfileView; label: string }[]>(
    () => [
      { id: 'list', label: t('skills.view.list') },
      { id: 'map', label: t('skills.view.map') },
    ],
    [t],
  )

  return (
    <div
      role="tablist"
      aria-label={t('skills.view.switcherAriaLabel')}
      className={cn(
        'inline-flex w-full rounded-full border border-[var(--color-surface-border)] bg-[var(--color-tone-mute-tint)] p-1 sm:w-auto',
        className,
      )}
    >
      {OPTIONS.map((option) => {
        const selected = value === option.id
        return (
          <button
            key={option.id}
            type="button"
            role="tab"
            aria-selected={selected}
            onClick={() => onChange(option.id)}
            className={cn(
              'min-h-11 flex-1 rounded-full px-4 text-[15px] font-semibold transition active:scale-95 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--color-primary-500)] focus-visible:ring-offset-2 sm:flex-none',
              selected
                ? 'bg-[var(--color-surface-bg)] text-[var(--color-primary-500)]'
                : 'text-[var(--color-surface-mute)]',
            )}
          >
            {option.label}
          </button>
        )
      })}
    </div>
  )
}

export default ViewSwitcher
